import { CheckCircle2, LoaderCircle, Mail, Phone } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useAppPreferences } from "../../lib/preferences";
import { submitWaitlist, validateWaitlist, WaitlistError, type WaitlistFieldErrors } from "./waitlist-client";

export function WaitlistForm() {
  const { t } = useAppPreferences();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState<WaitlistFieldErrors>({});
  const [failure, setFailure] = useState<WaitlistError | null>(null);
  const [pending, setPending] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [retryUntil, setRetryUntil] = useState<number | null>(null);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const inFlight = useRef(false);
  const completed = useRef(false);
  const request = useRef<AbortController | null>(null);
  const emailInput = useRef<HTMLInputElement>(null);
  const phoneInput = useRef<HTMLInputElement>(null);
  const consentInput = useRef<HTMLInputElement>(null);
  const failureMessage = useRef<HTMLDivElement>(null);
  const confirmation = useRef<HTMLDivElement>(null);

  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => { if (failure) failureMessage.current?.focus(); }, [failure]);
  useEffect(() => { if (accepted) confirmation.current?.focus(); }, [accepted]);
  useEffect(() => {
    if (retryUntil === null) return;
    const update = () => {
      const remaining = Math.max(0, Math.ceil((retryUntil - Date.now()) / 1000));
      setRetrySeconds(remaining);
      if (!remaining) setRetryUntil(null);
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [retryUntil]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current || completed.current || (retryUntil !== null && retryUntil > Date.now())) return;

    const result = validateWaitlist({ email, phone, consent, website });
    setErrors(result.errors);
    setFailure(null);
    if (!result.payload) {
      if (result.errors.email) emailInput.current?.focus();
      else if (result.errors.phone) phoneInput.current?.focus();
      else consentInput.current?.focus();
      return;
    }

    inFlight.current = true;
    setPending(true);
    const controller = new AbortController();
    request.current = controller;
    try {
      await submitWaitlist(result.payload, controller.signal);
      if (controller.signal.aborted) return;
      completed.current = true;
      setEmail("");
      setPhone("");
      setConsent(false);
      setWebsite("");
      setAccepted(true);
    } catch (error) {
      if (controller.signal.aborted) return;
      const problem = error instanceof WaitlistError ? error : new WaitlistError("unconfirmed");
      if (problem.reason === "rate-limited" && problem.retrySeconds && problem.retrySeconds > 0) {
        setRetrySeconds(problem.retrySeconds);
        setRetryUntil(Date.now() + problem.retrySeconds * 1000);
      }
      setFailure(problem);
    } finally {
      inFlight.current = false;
      if (!controller.signal.aborted) setPending(false);
    }
  }

  if (accepted) {
    return (
      <div className="join-confirmation" ref={confirmation} role="status" tabIndex={-1}>
        <CheckCircle2 aria-hidden="true" className="mb-6 text-[var(--color-accent)]" size={40} strokeWidth={1.5} />
        <h2 className="mb-3 text-2xl font-bold">{t("onboarding.success.title")}</h2>
        <p className="text-[var(--color-text-soft)]">{t("onboarding.success.body")}</p>
        <p className="mt-4 text-sm text-[var(--color-text-soft)]">{t("onboarding.success.note")}</p>
        <Link className="join-text-link mt-7" to="/">{t("onboarding.back")}</Link>
      </div>
    );
  }

  const failureKey = failure?.reason === "invalid" ? "onboarding.error.invalid"
    : failure?.reason === "unavailable" ? "onboarding.error.unavailable"
    : failure?.reason === "rate-limited" ? "onboarding.error.rateLimited"
    : "onboarding.error.unconfirmed";

  return (
    <form aria-labelledby="waitlist-title" aria-busy={pending} noValidate onSubmit={handleSubmit}>
      <div className="mb-7">
        <h2 className="mb-2 text-2xl font-bold" id="waitlist-title">{t("onboarding.form.title")}</h2>
        <p className="text-sm text-[var(--color-text-soft)]" id="waitlist-contact-hint">{t("onboarding.form.body")}</p>
      </div>
      {Object.keys(errors).length > 0 && (
        <div className="join-error mb-5" role="alert">{t("onboarding.error.review")}</div>
      )}
      {failure && (
        <div className="join-error mb-5" ref={failureMessage} role="alert" tabIndex={-1}>
          {t(failureKey)}
        </div>
      )}
      {retrySeconds > 0 && (
        <p className="mb-5 text-sm text-[var(--color-text-soft)]" role="timer">
          {t("onboarding.retryAfter", { seconds: retrySeconds })}
        </p>
      )}
      <fieldset className="m-0 min-w-0 border-0 p-0" disabled={pending}>
        <legend className="sr-only">{t("onboarding.form.contact")}</legend>
        <div className="mb-5">
          <label className="join-label" htmlFor="waitlist-email">
            <Mail aria-hidden="true" size={17} /> {t("onboarding.form.email")}
          </label>
          <input
            aria-describedby={`waitlist-contact-hint${errors.email ? " waitlist-email-error" : ""}`}
            aria-invalid={Boolean(errors.email)}
            autoCapitalize="none" autoComplete="email" className="join-input" dir="ltr"
            id="waitlist-email" maxLength={254} name="email" onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com" ref={emailInput} required spellCheck={false} type="email" value={email}
          />
          {errors.email && <p className="join-field-error" id="waitlist-email-error">{t(errors.email)}</p>}
        </div>
        <div className="mb-6">
          <label className="join-label" htmlFor="waitlist-phone">
            <Phone aria-hidden="true" size={17} /> {t("onboarding.form.phone")}
          </label>
          <input
            aria-describedby={`waitlist-phone-hint${errors.phone ? " waitlist-phone-error" : ""}`}
            aria-invalid={Boolean(errors.phone)}
            autoComplete="tel" className="join-input" dir="ltr" id="waitlist-phone" inputMode="tel"
            maxLength={40} name="phone" onChange={(event) => setPhone(event.target.value)}
            placeholder="+441234567890" ref={phoneInput} required type="tel" value={phone}
          />
          <p className="mt-2 text-xs text-[var(--color-text-soft)]" id="waitlist-phone-hint">{t("onboarding.form.phoneHint")}</p>
          {errors.phone && <p className="join-field-error" id="waitlist-phone-error">{t(errors.phone)}</p>}
        </div>
        <div aria-hidden="true" hidden>
          <label htmlFor="waitlist-website">Website</label>
          <input autoComplete="off" id="waitlist-website" name="website" onChange={(event) => setWebsite(event.target.value)} tabIndex={-1} type="text" value={website} />
        </div>
        <label className="join-consent" htmlFor="waitlist-consent">
          <input
            aria-describedby={errors.consent ? "waitlist-consent-error" : undefined}
            aria-invalid={Boolean(errors.consent)} checked={consent} id="waitlist-consent" name="consent"
            onChange={(event) => setConsent(event.target.checked === true)} ref={consentInput} required type="checkbox"
          />
          <span>{t("onboarding.form.consent")}</span>
        </label>
        {errors.consent && <p className="join-field-error" id="waitlist-consent-error">{t(errors.consent)}</p>}
        <button className="join-submit mt-6" disabled={pending || retrySeconds > 0} type="submit">
          {pending && <LoaderCircle aria-hidden="true" className="animate-spin motion-reduce:animate-none" size={18} />}
          {t(pending ? "onboarding.form.sending" : "onboarding.form.submit")}
        </button>
      </fieldset>
      <p aria-live="polite" className="sr-only" role="status">{pending ? t("onboarding.form.sending") : ""}</p>
      <p className="mt-4 text-xs text-[var(--color-text-soft)]">{t("onboarding.form.dataPolicy")}</p>
      <p className="text-xs text-[var(--color-text-soft)]">
        {t("onboarding.form.deletion")}{" "}
        <a className="join-text-link" dir="ltr" href="mailto:support@gaamed.com">support@gaamed.com</a>.
      </p>
      <p className="mt-4 text-xs text-[var(--color-text-soft)]">
        {t("onboarding.form.privacy")} <Link className="join-text-link" to="/legal/privacy#app-updates-contacts">{t("landing.footer.privacy")}</Link>
      </p>
    </form>
  );
}
